import {useEffect, useCallback, useEffectEvent, useRef, useState} from 'react'

type UseAutoScrollOptions = {
  /** The scroll container element. */
  containerRef: React.RefObject<HTMLElement | null>
  /** A sentinel element at the bottom of scrollable content. */
  sentinelRef: React.RefObject<HTMLElement | null>
  /** The content element whose height grows (observed for auto-scroll). */
  contentRef: React.RefObject<HTMLElement | null>
  /** Resets scroll tracking state when this value changes (e.g., task navigation). */
  resetKey?: string
  /** IntersectionObserver rootMargin offset in px — how far from the bottom the sentinel must be to count as "at bottom". Default 50. */
  offset?: number
}

/**
 * Unified auto-scroll hook that:
 * 1. Tracks whether the user is scrolled to the bottom (IntersectionObserver on sentinel)
 * 2. Auto-scrolls when content grows while already at bottom (ResizeObserver)
 * 3. Exposes a scrollToBottom function
 *
 * Auto-scroll only activates after the user has scrolled at least once, preventing
 * unwanted scroll-to-bottom on initial page load.
 */
export function useAutoScroll({containerRef, sentinelRef, contentRef, resetKey, offset = 50}: UseAutoScrollOptions) {
  const [isAtBottom, setIsAtBottom] = useState(false)
  const hasScrolledRef = useRef(false)

  // Reset state when resetKey changes
  const [prevResetKey, setPrevResetKey] = useState(resetKey)
  if (resetKey !== undefined && prevResetKey !== resetKey) {
    setPrevResetKey(resetKey)
    setIsAtBottom(false)
  }

  // Track whether the user has scrolled — resets on resetKey change
  useEffect(() => {
    hasScrolledRef.current = false
    const container = containerRef.current
    if (!container) return

    const handleScroll = () => {
      hasScrolledRef.current = true
    }

    // eslint-disable-next-line github/prefer-observers
    container.addEventListener('scroll', handleScroll, {once: true})
    return () => container.removeEventListener('scroll', handleScroll)
  }, [containerRef, resetKey])

  // IntersectionObserver: track if sentinel is visible (user is at bottom)
  useEffect(() => {
    const sentinel = sentinelRef.current
    const container = containerRef.current
    if (!sentinel || !container) return

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry) {
          setIsAtBottom(entry.isIntersecting)
        }
      },
      {
        root: container,
        rootMargin: `0px 0px ${offset}px 0px`,
        threshold: 0,
      },
    )

    io.observe(sentinel)
    return () => io.disconnect()
  }, [containerRef, sentinelRef, offset, resetKey])

  // Auto-scroll when content grows — only if user has scrolled and is at bottom
  const onContentResize = useEffectEvent(() => {
    if (hasScrolledRef.current && isAtBottom) {
      sentinelRef.current?.scrollIntoView({behavior: 'instant', block: 'end'})
    }
  })

  // ResizeObserver: auto-scroll when content grows and user is at bottom
  useEffect(() => {
    const content = contentRef.current
    if (!content) return

    const ro = new ResizeObserver(() => onContentResize())
    ro.observe(content)
    return () => ro.disconnect()
  }, [contentRef, resetKey])

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'smooth') => {
      sentinelRef.current?.scrollIntoView({behavior, block: 'end'})
    },
    [sentinelRef],
  )

  return {isAtBottom, scrollToBottom} as const
}
